import React, { useState, FC, useEffect, useContext } from 'react';
import {
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Popover,
  Select,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import NumberFormat from 'react-number-format';
import { useApolloClient } from '@apollo/react-hooks';

import colors from '../../../../themes/main-colors';
import { useTranslation } from '../../../../hooks/useTranslation';
import IconPhone from '../../../../static/PhoneIcon.svg';
import IconAccount from '../../../../static/AccountIcon.svg';
import SearchIcon from '@material-ui/icons/Search';
import {
  OutageLookUpType,
  OutageLookupParams,
  OutageLookupResponse,
} from '../../../../__generated__/pge-types';
import { useIsMobile } from '../../../../util/style-utils';
import { setInStorage, getFromStorage } from '../../../../util/storage-utils';
import {
  displayPhoneNumberFormat,
  parsePhoneNumber,
} from '../../../../util/format';
import AccountTextField from '../../../account-text-field';
import PhoneTextField from '../../../phone-text-field';
import { useOutageLookUp } from '../hooks/useOutageLookUp';
import { TransitionContext } from '../../../../providers/TransitionProvider';
import { NotificationsContext } from '../../../../providers/NotificationsProvider';
import validationRules, {
  Model,
} from '../../outage-location-lookup/src/alternateLookupOption.rules';
import useFormState from '../../../../hooks/useFormState';
import { NotificationRequestAccountParams } from '../../OutageCenterPage';
import ROUTES from '../../../../routes';
import useOutageCenterUtils from '../utils';

const Pop = (props: any) => {
  const { className, anchorEl, style, ...rest } = props;
  return (
    <div
      {...rest}
      style={{
        position: 'absolute',
        zIndex: 9999,
        width: '100%',
        left: 0,
      }}
    />
  );
};

export const moveCursorToEnd = (
  element: any,
  inputValue?: any,
  len?: number,
) => {
  // To move the cursor at end of the text in text input field
  const input = element?.current;
  const value = inputValue
    ? parsePhoneNumber(inputValue)
    : input && input.value && parsePhoneNumber(input.value);
  const length = len ? len : 20;
  if (value) {
    // Mostly for Web Browsers
    if (input.setSelectionRange) {
      input.focus();
      input.setSelectionRange(length, length);
    } else if (input.createTextRange) {
      const t = input.createTextRange();
      t.collapse(true);
      t.moveEnd('character', length);
      t.moveStart('character', length);
      t.select();
    }
  }
};
interface NumberFormatCustomProps {
  inputRef: (instance: NumberFormat | null) => void;
  onChange: (event: { target: { value: string } }) => void;
}

const PhoneNumberFormat = (props: NumberFormatCustomProps | any) => {
  const { inputRef, onChange, ...other } = props;

  return (
    <NumberFormat
      data-testid={'phone-input'}
      {...other}
      getInputRef={inputRef}
      onPaste={() => {
        window.getSelection()?.collapseToStart();
      }}
      onValueChange={values => {
        if (values.value !== other.value) {
          onChange({
            target: {
              value: values.value,
              name: props.name,
            },
          });
        }
      }}
      type={'tel'}
      format="(###) ###-####"
      mask="_"
      allowNegative={false}
      thousandSeparator
      isNumericString
    />
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    headerWrapper: {
      height: '100%',
    },
    headerBody: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
    },
    selectionDropdown: {
      height: '100%',
      borderRight: `2px solid ${colors.disableColor}`,
      paddingRight: theme.spacing(0.875),
      [theme.breakpoints.down('sm')]: {
        borderRight: 0,
      },
    },
    inputData: {
      width: '100%',
      paddingLeft: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        marginLeft: -28,
        paddingLeft: 0,
      },
    },
    formControl: {
      paddingLeft: theme.spacing(1.625),
      paddingRight: theme.spacing(1.625),
      width: '100%',
    },
    selectTagStyles: {
      height: '100%',
      color: colors.brightBlue,
      fontSize: '1rem',
      lineHeight: '1.25rem',
      [theme.breakpoints.down('sm')]: {
        paddingLeft: theme.spacing(1.25),
      },
    },
    listItems: {
      color: colors.brightBlue,
      fontSize: '1rem',
      lineHeight: '1.25rem',
    },
    listItemIcon: {
      minWidth: 40,
      '& .a': {
        fill: colors.brightBlue,
        opacity: 1,
      },
    },
    arrowDownIcon: {
      color: colors.brightBlue,
      [theme.breakpoints.down('sm')]: {
        right: 26,
      },
    },
    selectControl: {
      paddingLeft: theme.spacing(2),
      marginRight: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        marginRight: 0,
        paddingLeft: 0,
      },
      '&:focus': {
        backgroundColor: colors.white,
      },
    },
    textField: {
      width: '100%',
      fontSize: '1rem',
      lineHeight: '1.25rem',
    },
    '&.MuiListItemIcon-root': {
      minWidth: 40,
      color: colors.brightBlue,
    },
    backIconMob: {
      color: colors.aluminiumColor,
      fontSize: '.75rem',
      marginRight: theme.spacing(0.625),
    },
    lookUpIcon: {
      '& .a': {
        fill: colors.brightBlue,
        opacity: 1,
      },
    },
    locationSearchIconInActive: {
      color: colors.midGrey,
    },
    locationSearchIconActive: {
      color: colors.brightBlue,
      opacity: 1,
    },
    expandMoreIconStyle: {
      position: 'absolute',
      right: 0,
      color: colors.brightBlue,
      pointerEvents: 'none',
      [theme.breakpoints.down('sm')]: {
        right: 25,
      },
    },
  }),
);

const useStylesAutoComplete = makeStyles((theme: Theme) =>
  createStyles({
    groupLabel: {
      '&.MuiAutocomplete-groupLabel': {
        top: 0,
        color: colors.noirBlur,
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: '1.125rem',
        position: 'absolute',
        width: '100%',
        left: 0,
        paddingLeft: theme.spacing(2.5),
        marginTop: theme.spacing(2.5),
      },
    },
    groupUl: {
      '&.MuiAutocomplete-groupUl .MuiAutocomplete-option': {
        paddingTop: theme.spacing(1.25),
        paddingBottom: theme.spacing(1.25),
        paddingLeft: 0,
        minHeight: 0,
        whiteSpace: 'nowrap',
      },
    },
    listbox: {
      '&.MuiAutocomplete-listbox': {
        paddingLeft: theme.spacing(2.5),
        color: colors.brightBlue,
        fontSize: '1rem',
        lineHeight: '1.125rem',
        marginTop: theme.spacing(4.375),
      },
    },
    inputRoot: {
      backgroundColor: colors.white,
      '&[class*="MuiFilledInput-root"]': {
        paddingTop: 0,
        paddingLeft: 0,
      },
      '&[class*="MuiFilledInput-root"] .MuiAutocomplete-input': {
        padding: '5px 0px',
      },
      '&[class*="MuiInput-root"] .MuiAutocomplete-input:first-child': {
        paddingTop: 0,
      },
      '&.MuiFilledInput-root:hover': {
        backgroundColor: colors.white,
      },
      '&.MuiFilledInput-root.Mui-focused': {
        backgroundColor: colors.white,
      },
    },
  }),
);

const defaultFormValues: Model = {
  lookUpType: OutageLookUpType.Premise,
  lookUpPhoneNumber: '',
  lookUpAccountNumber: '',
};
type Props = {
  outageLookUpResult: (
    data: OutageLookupResponse,
    lookUpType: OutageLookUpType,
    lookUpValue: string,
  ) => void;
  setLoggedInUserNotificationRequestAccountParams: React.Dispatch<
    React.SetStateAction<NotificationRequestAccountParams>
  >;
};

const OutageMapHeader: FC<Props> = ({
  outageLookUpResult,
  setLoggedInUserNotificationRequestAccountParams,
}: Props) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const [lookUpValue, setLookUpValue] = useState('');
  const [searchList, setSearchList] = useState([]);
  const [lookUpType, setLookUpType] = React.useState(OutageLookUpType.Phone);
  const [text, setText] = React.useState(t('PHONE'));
  const [open, setOpen] = useState(false);
  const [autoSelectOpen, setAutoSelectOpen] = useState(false);
  const [outageDetail, setOutageDetail] = useState<OutageLookupResponse>({
    isAccountExists: false,
    encryptedAccountNumber: '',
    isEligibleToReport: false,
  });
  const { setState: updateTransition } = useContext(TransitionContext);

  const apolloClient = useApolloClient();

  const notificationContext = useContext(NotificationsContext);
  const alternateLookupForm = useFormState(defaultFormValues, {
    validate: validationRules,
    validationContext: {
      apolloClient,
    },
  });
  const selectRef = React.useRef();
  const autoCompleteInput = React.useRef<HTMLInputElement>();

  const recentPhoneSearches = 'recentPhoneSearches';
  const recentAccountSearches = 'recentAccountSearches';
  const isBrowser = !(typeof window === 'undefined');
  const isOutagesMobileApp =
    isBrowser &&
    window.location.pathname.replace(/\/$/, '') === ROUTES.OUTAGES_MOBILE_APP;
  const isFullSurveyPage =
    isBrowser &&
    window.location.pathname.replace(/\/$/, '') === ROUTES.HOME_ENERGY_ANALYSIS;
  const isPGEPLUSGetStarted =
    isBrowser &&
    window.location.pathname.replace(/\/$/, '') === ROUTES.PGE_PLUS_GET_STARTED;
  const { getOutageLookUpQueryResponse } = useOutageLookUp();
  const { setErrorNotification } = useOutageCenterUtils();

  const localStorageManageSetUp = (key: any) => {
    const dataList: any = getFromStorage(key)
      ? JSON.parse(getFromStorage(key))
      : [];
    if (lookUpValue && !dataList.includes(lookUpValue)) {
      dataList.push(lookUpValue);
      setInStorage(key, JSON.stringify(dataList));
      if (lookUpType === OutageLookUpType.Phone) {
        localStorageManageSetUpPhone(key);
      } else {
        setSearchList(JSON.parse(getFromStorage(key)));
      }
    }
  };

  const localStorageManageSetUpPhone = (key: any) => {
    const searchListData = getFromStorage(key)
      ? JSON.parse(getFromStorage(key))
      : [];
    const searchListPhoneNumberFormat: any = [];
    searchListData?.map((item: any) => {
      searchListPhoneNumberFormat.push(item);
    });
    setSearchList(searchListPhoneNumberFormat);
  };

  const showError = () => {
    setErrorNotification(true);
    updateTransition({ open: false });
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  const getOutageDetail = async (params: OutageLookupParams) => {
    updateTransition({ open: true });
    try {
      const getOutageLookupResponse = await getOutageLookUpQueryResponse(
        params,
      );
      if (
        Boolean(getOutageLookupResponse) &&
        getOutageLookupResponse?.isEligibleToReport !== null &&
        getOutageLookupResponse?.isEligibleToReport !== undefined
      ) {
        outageLookUpResult(getOutageLookupResponse, lookUpType, lookUpValue);
        updateTransition({ open: false });
      } else {
        showError();
      }
    } catch {
      showError();
    }
  };

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    autoCompleteInput?.current?.blur();
    open && setOpen(false);
    autoSelectOpen && setAutoSelectOpen(false);
    setLoggedInUserNotificationRequestAccountParams({
      accountNumber: '',
      encryptedPersonId: '',
    });
    if (lookUpValue && lookUpValue.length === 10) {
      lookUpType === OutageLookUpType.Phone
        ? localStorageManageSetUp(recentPhoneSearches)
        : localStorageManageSetUp(recentAccountSearches);
      const params: OutageLookupParams = {
        lookupType: lookUpType,
        value:
          lookUpType === OutageLookUpType.Phone
            ? displayPhoneNumberFormat(lookUpValue)
            : lookUpValue,
      };
      try {
        await getOutageDetail(params);
      } catch {
        showError();
      }
    } else {
      return null;
    }
  };

  const autoSelectClick = () => {
    if (!autoSelectOpen) {
      setAutoSelectOpen(true);
    }
    open && setOpen(false);
    return null;
  };

  useEffect(() => {
    if (lookUpType === OutageLookUpType.Phone) {
      localStorageManageSetUpPhone(recentPhoneSearches);
    }
    if (lookUpType === OutageLookUpType.Account) {
      getFromStorage(recentAccountSearches)
        ? setSearchList(JSON.parse(getFromStorage(recentAccountSearches)))
        : setSearchList([]);
    }
  }, [lookUpType]);

  const handleSelection: any = (newType: any, newText: any) => {
    lookUpType !== newType ? (lookUpValue ? setLookUpValue('') : '') : '';
    setLookUpType(newType);
    setText(newText);
    setOpen(false);
  };
  const onChangeAutoSelect = (e: any) => {
    setAutoSelectOpen(true);
    open && setOpen(false);
    setLookUpValue(e.target.value);
  };
  const setSelectOpen = () => {
    setOpen(true);
    autoSelectOpen && setAutoSelectOpen(false);
  };
  const autoCompleteStyles = useStylesAutoComplete();
  const renderOptionDisplay = (value: any) => {
    if (value && lookUpType === OutageLookUpType.Phone) {
      return displayPhoneNumberFormat(value);
    } else {
      return value;
    }
  };

  const onPasteHandle = async (e: any) => {
    const value = e.clipboardData.getData('text/plain');
    if (
      lookUpType === OutageLookUpType.Account &&
      value &&
      parsePhoneNumber(value).length === 10
    ) {
      await setLookUpValue(parsePhoneNumber(value));
      moveCursorToEnd(autoCompleteInput);
    }
  };

  const onCopyHandle = async (e: any) => {
    e.clipboardData.setData('text/plain', e.target.value);
  };

  const onCutHandle = async (e: any) => {
    e.clipboardData.setData('text/plain', e.target.value);
  };

  const onChangeInputHandle = async (e: any) => {
    setLookUpValue(parsePhoneNumber(e.target.value));
    moveCursorToEnd(autoCompleteInput, parsePhoneNumber(e.target.value));
  };

  const onchangeEvent = async (newValue: any) => {
    await setLookUpValue(newValue);
    moveCursorToEnd(autoCompleteInput);
  };

  const onBlurEventHandle = () => {
    autoCompleteInput?.current?.blur();
  };

  return (
    <Grid className={classes.headerWrapper}>
      <form className={classes.headerBody} onSubmit={handleSubmit}>
        <Grid className={classes.selectionDropdown}>
          <Select
            ref={selectRef}
            value={lookUpType}
            displayEmpty
            open={false}
            IconComponent={() => (
              <ExpandMoreIcon className={classes.expandMoreIconStyle} />
            )}
            data-testid={'select-test-id'}
            inputProps={{ id: 'select', 'data-testid': 'select-id' }}
            disableUnderline
            className={classes.selectTagStyles}
            classes={{
              root: classes.selectControl,
              icon: classes.arrowDownIcon,
            }}
            onOpen={setSelectOpen}
            startAdornment={
              isMobile ? (
                <InputAdornment position="start">
                  {autoSelectOpen &&
                  (isOutagesMobileApp ||
                    isPGEPLUSGetStarted ||
                    isFullSurveyPage) ? (
                    <ArrowBackIosIcon
                      className={classes.backIconMob}
                      onClick={() => setAutoSelectOpen(false)}
                    />
                  ) : null}
                  {lookUpType === OutageLookUpType.Phone ? (
                    <IconPhone className={classes.lookUpIcon} />
                  ) : (
                    <IconAccount className={classes.lookUpIcon} />
                  )}
                </InputAdornment>
              ) : null
            }
          >
            <MenuItem value={lookUpType}>{!isMobile ? text : ''}</MenuItem>
          </Select>
          <Popover
            id="popover-select-lookUpType"
            open={open}
            anchorEl={selectRef.current}
            onClose={() => handleSelection(lookUpType, text)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            {isMobile ? (
              <ListSubheader
                style={{ color: colors.brightBlue, fontSize: '1.5rem' }}
              >
                {t('SEARCH_BY')}
              </ListSubheader>
            ) : null}
            <MenuItem
              value={OutageLookUpType.Phone}
              onClick={() =>
                handleSelection(
                  OutageLookUpType.Phone,
                  !isMobile ? t('PHONE') : '',
                )
              }
            >
              {isMobile ? (
                <ListItemIcon className={classes.listItemIcon}>
                  <IconPhone />
                </ListItemIcon>
              ) : null}
              <ListItemText
                className={classes.listItems}
                primary={isMobile ? t('SEARCH_BY_PHONE') : t('PHONE_NUMBER')}
              />
            </MenuItem>
            <MenuItem
              value={OutageLookUpType.Account}
              onClick={() =>
                handleSelection(
                  OutageLookUpType.Account,
                  !isMobile ? t('ACCOUNT') : '',
                )
              }
            >
              {isMobile ? (
                <ListItemIcon className={classes.listItemIcon}>
                  <IconAccount />
                </ListItemIcon>
              ) : null}
              <ListItemText
                className={classes.listItems}
                primary={
                  isMobile ? t('SEARCH_BY_ACCOUNT') : t('ACCOUNT_NUMBER')
                }
              />
            </MenuItem>
          </Popover>
        </Grid>

        <Grid className={classes.inputData}>
          <Autocomplete
            id="autocomplete-lookUpValue"
            data-testid="autocomplete-search"
            freeSolo={true}
            classes={autoCompleteStyles}
            PopperComponent={Pop}
            options={searchList}
            open={autoSelectOpen}
            value={lookUpValue}
            onChange={async (event: any, newValue: any) => {
              await onchangeEvent(newValue);
            }}
            onBlur={onBlurEventHandle}
            openOnFocus
            selectOnFocus={true}
            onClose={() => {
              setAutoSelectOpen(false);
              moveCursorToEnd(autoCompleteInput, lookUpValue);
            }}
            style={{ width: '100%' }}
            groupBy={() => t('RECENT_SEARCHES')}
            renderOption={(option: any) => renderOptionDisplay(option)}
            renderInput={params =>
              lookUpType === OutageLookUpType.Phone ? (
                <PhoneTextField
                  {...params}
                  label={''}
                  inputRef={autoCompleteInput}
                  inputProps={{
                    ...params.inputProps,
                  }}
                  variant="filled"
                  fullWidth
                  autoComplete={'telephone'}
                  placeholder={
                    isMobile
                      ? t('SEARCH_BY_PHONE_NUMBER')
                      : t('LOOKUP_BY_PHONE_NUMBER')
                  }
                  onClick={autoSelectClick}
                  onChange={(e: any) => onChangeAutoSelect(e)}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    inputComponent: PhoneNumberFormat as any,
                  }}
                  margin={'none'}
                  onPaste={async (e: any) => {
                    await onPasteHandle(e);
                  }}
                  onCopy={async (e: any) => {
                    await onCopyHandle(e);
                  }}
                  onCut={async (e: any) => {
                    await onCutHandle(e);
                  }}
                  onFocus={async (e: any) => {
                    await onChangeInputHandle(e);
                  }}
                />
              ) : (
                <AccountTextField
                  {...params}
                  label={''}
                  inputRef={autoCompleteInput}
                  inputProps={{
                    ...params.inputProps,
                    maxLength: 10,
                    minLength: 10,
                    type: 'tel',
                  }}
                  variant="filled"
                  fullWidth
                  placeholder={
                    isMobile
                      ? t('SEARCH_BY_ACCOUNT_NUMBER')
                      : t('LOOKUP_BY_ACCOUNT_NUMBER')
                  }
                  onClick={autoSelectClick}
                  onChange={(e: any) => onChangeAutoSelect(e)}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                  margin={'none'}
                  onPaste={async (e: any) => {
                    await onPasteHandle(e);
                  }}
                  onCopy={async (e: any) => {
                    await onCopyHandle(e);
                  }}
                  onCut={async (e: any) => {
                    await onCutHandle(e);
                  }}
                  onFocus={async (e: any) => {
                    await onChangeInputHandle(e);
                  }}
                />
              )
            }
          />
        </Grid>
        <Grid>
          <IconButton
            aria-label="location-search-icon"
            disabled={lookUpValue && lookUpValue.length === 10 ? false : true}
            type="submit"
          >
            <SearchIcon
              className={
                lookUpValue && lookUpValue.length === 10
                  ? classes.locationSearchIconActive
                  : classes.locationSearchIconInActive
              }
            />
          </IconButton>
        </Grid>
      </form>
    </Grid>
  );
};

export default OutageMapHeader;
